import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NEO_DIALOG_DATA, NeoDialogModule, NeoDialogRef } from '@neogov/pattern-library/neo-dialog';
import { filter, firstValueFrom, isObservable } from 'rxjs';
import { ActionButtonComponent } from '../action-button';
import { ButtonComponent } from '../button';
import { IconComponent } from '../icon';
import { ConfirmationAction, ConfirmationModalConfig } from './confirmation-modal.types';

/** Cancel action used when the config does not provide one. */
const DEFAULT_CANCEL_ACTION: ConfirmationAction<string> = {
  label: 'Cancel',
  value: 'cancel',
  autIdSuffix: 'cancel-btn',
  variant: 'basic',
  color: 'primary',
};

/**
 * Single shell for every confirmation dialog in the app.
 *
 * Structure comes from the Pattern Library dialog directives (`neo-dialog-title-container`,
 * `neo-dialog-title`, `neo-dialog-content`, `neo-dialog-actions`) so layout, scrolling and the
 * `aria-labelledby` wiring are handled by the design system.
 *
 * Never opened directly: use `ConfirmationProvider`, which owns the dialog configuration.
 */
@Component({
  selector: 'cm-confirmation-modal',
  standalone: true,
  imports: [
    NgComponentOutlet,
    NgTemplateOutlet,
    NeoDialogModule,
    ActionButtonComponent,
    ButtonComponent,
    IconComponent,
  ],
  templateUrl: './confirmation-modal.component.html',
  styleUrl: './confirmation-modal.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationModalComponent {
  private readonly _dialogRef =
    inject<NeoDialogRef<ConfirmationModalComponent, string>>(NeoDialogRef);

  public readonly config = inject<ConfirmationModalConfig<string>>(NEO_DIALOG_DATA);

  /** `autIdSuffix` of the action whose async handler is currently running. */
  private readonly _runningAction = signal<string | null>(null);
  public readonly runningAction = this._runningAction.asReadonly();
  public readonly isBusy = computed(() => this._runningAction() !== null);

  /** Referenced by `ariaDescribedBy` on the dialog config. */
  public readonly descriptionId = `${this.config.autId}-description`;
  public readonly showCloseIcon = this.config.showCloseIcon ?? true;

  /** Footer buttons, left to right: cancel, secondary, confirm. */
  public readonly actions: ConfirmationAction<string>[] = this._buildActions();

  /** Value returned when the user dismisses instead of picking an action. */
  private readonly _dismissValue: string = this._resolveDismissValue();

  constructor() {
    // The provider opens with `disableClose: true` so the CDK never closes the dialog with
    // `undefined`. Dismissal is handled here instead, always resolving to the cancel value.
    this._dialogRef
      .keydownEvents()
      .pipe(
        filter((event) => event.key === 'Escape'),
        takeUntilDestroyed(),
      )
      .subscribe((event) => {
        event.preventDefault();
        this.dismiss();
      });

    this._dialogRef
      .backdropClick()
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.dismiss());
  }

  /**
   * Runs an action. Synchronous actions close immediately; asynchronous ones keep the dialog
   * open with the button in a loading state until they settle.
   */
  public async run(action: ConfirmationAction<string>): Promise<void> {
    if (this.isBusy()) return;

    if (!action.handler) {
      this._closeAfter(action);
      return;
    }

    const previousDisableClose = this._dialogRef.disableClose;
    this._runningAction.set(action.autIdSuffix);
    this._dialogRef.disableClose = true;

    try {
      const outcome = action.handler();

      if (isObservable(outcome)) {
        await firstValueFrom(outcome);
      } else if (outcome instanceof Promise) {
        await outcome;
      }

      this._closeAfter(action);
    } catch (error) {
      this.config.onActionError?.(error, action);
    } finally {
      this._runningAction.set(null);
      this._dialogRef.disableClose = previousDisableClose;
    }
  }

  /** Close button, ESC and backdrop click. */
  public dismiss(): void {
    if (this.isBusy() || this.config.disableClose) return;

    this._dialogRef.close(this._dismissValue);
  }

  private _closeAfter(action: ConfirmationAction<string>): void {
    if (action.closeOnClick === false) return;

    this._dialogRef.close(action.value);
  }

  private _buildActions(): ConfirmationAction<string>[] {
    const actions: ConfirmationAction<string>[] = [];
    const cancel = this._resolveCancelAction();

    if (cancel) actions.push(cancel);
    if (this.config.secondary) actions.push(this.config.secondary);
    actions.push(this.config.confirm);

    return actions;
  }

  private _resolveCancelAction(): ConfirmationAction<string> | null {
    if (this.config.cancel === false) return null;

    return this.config.cancel ?? DEFAULT_CANCEL_ACTION;
  }

  private _resolveDismissValue(): string {
    return this._resolveCancelAction()?.value ?? DEFAULT_CANCEL_ACTION.value;
  }
}
